import moment from 'moment-timezone';
import React, { memo, useMemo } from 'react';
import classNames from 'classnames';
import BaseModal from '../../../components/modals/BaseModal/BaseModal';
import { useGuaranteeMutation } from '../../../redux/guaranteeShifts';
import infoIcon from '../../../assets/images/icons8-info.svg';

import './GuaranteeSlotsModal.scss';

const GuaranteeSlotsModal = ({
	shiftData,
	slots = [],
	onSaveChanges = () => {},
	onCancel = () => {},
	onError,
	setIsLoading
}) => {
	const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

	const startDate = moment(shiftData.start_time_at_utc).tz(shiftData.facility_timezone);
	const endDate = moment(shiftData.end_time_at_utc || null).tz(shiftData.facility_timezone || null);

	const currentSlots = useMemo(() => {
		return slots.filter((s) => !s.is_flex);
	}, [slots]);

	const isGuaranteed = currentSlots.length > 0 && currentSlots.every((slot) => slot.is_guaranteed);
	const isMultipleSlots = currentSlots.length > 1;

	const title = !isGuaranteed
		? `Guarantee ${isMultipleSlots ? 'all open On Demand shifts' : 'this open shift'}`
		: `Remove Guarantee from ${isMultipleSlots ? 'all open On Demand shifts' : 'this open shift'}`;

	const [guaranteeShift] = useGuaranteeMutation();

	const onGuaranteeShift = async (newGuaranteedStatus) => {
		if (currentSlots.length === 0) {
			return;
		}
		if (setIsLoading) {
			setIsLoading(true);
		}
		const [firstSlot] = currentSlots;
		const owningShiftId = firstSlot.shift_id ?? shiftData.shift_id;
		const slotValues = currentSlots.reduce((acc, slot) => {
			acc[slot.slot_id] = newGuaranteedStatus;
			return acc;
		}, {});
		try {
			await guaranteeShift({
				shiftId: owningShiftId,
				body: {
					isRecurring: firstSlot.isRecurring ?? shiftData.isRecurring,
					slots: slotValues,
					shiftDate: moment.utc(firstSlot.raw_start_time ?? shiftData.raw_start_time).format('YYYY-MM-DD')
				}
			}).unwrap();
			onSaveChanges(newGuaranteedStatus);
		} catch (e) {
			if (onError) {
				onError(e);
			}
		} finally {
			if (setIsLoading) {
				setIsLoading(false);
			}
		}
	};

	return (
		<BaseModal
			key="guarantee-slots-modal"
			title={title}
			className={classNames(`guarantee-slots-modal`, { 'is-guaranteeing': !isGuaranteed })}
			primaryActionLabel="Confirm"
			onPrimaryAction={() => onGuaranteeShift(!isGuaranteed)}
			secondaryActionLabel="Cancel"
			onSecondaryAction={onCancel}
			onHide={onCancel}
			confirmNewModal
		>
			<div className="guarantee-modal-shift-details">
				<div>
					Facility: <b>{shiftData.facility_name}</b>
				</div>
				<div>
					Shift type: <b>{shiftData.resource_type}</b>
				</div>
				<div>
					Date:{' '}
					<b>
						{dayNames[startDate.day()]}, {startDate.format('MM/DD/YYYY')}
					</b>
				</div>
				<div>
					Shift time:{' '}
					<b>
						{startDate.format('h:mm A')} - {endDate.format('h:mm A')}
					</b>
				</div>
			</div>

			<div className="warning-banner">
				<img src={infoIcon} alt="info" className="warning-icon" />
				<div>
					<b>
						{' '}
						{!isGuaranteed
							? 'Guaranteed Shifts® are picked up with a 20% higher claim rate and have higher show rates.'
							: 'Your fulfillment rate may decrease significantly! Guaranteed Shifts® are picked up with a 20% higher claim rate and have higher show rates.'}
					</b>{' '}
					{!isGuaranteed
						? 'If this shift is claimed by a ShiftMed professional, you are committing to pay for the caregiver, even if you choose to cancel them later.'
						: 'This change will only apply to open On Demand shifts that are currently Guaranteed. If your Guaranteed Shift® has already been claimed, you are still committed to pay for the caregiver, even if you choose to cancel them later.'}
				</div>
			</div>
		</BaseModal>
	);
};

export default memo(GuaranteeSlotsModal);
